import { format, intlFormat, isValid } from 'date-fns';

import { I18nLocale } from '#/i18n/types/I18nLocale';

import { type DateInput, normalizeDateInput } from './domain';
import { getDateFnsLocale, getLocaleCode } from './locale';

function formatWithDateFnsPattern({
  date,
  locale,
  pattern,
}: {
  date: DateInput;
  locale: I18nLocale;
  pattern: string;
}) {
  return format(normalizeDateInput(date), pattern, {
    locale: getDateFnsLocale(locale),
  });
}

function formatTime(
  date: DateInput,
  localeCode: string,
  params: Parameters<typeof Intl.DateTimeFormat>[1] = Object.freeze({}),
) {
  const { timeZone = undefined, ...spreadParams } = params;
  return new Date(date).toLocaleTimeString(localeCode, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timeZone ?? undefined,
    ...spreadParams,
  });
}

export function formatInTimeZone({
  instant,
  locale,
  timeZone,
  displayTimezone,
}: {
  instant: DateInput;
  locale: I18nLocale;
  timeZone?: string | null;
  displayTimezone?: boolean;
}) {
  const options: Parameters<typeof Intl.DateTimeFormat>[1] = {
    timeZoneName: displayTimezone ? 'short' : undefined,
    timeZone: timeZone ?? undefined,
  };

  return formatTime(instant, getLocaleCode(locale), options);
}

function formatCalendarDateWithIntl({
  date,
  locale,
  timezone,
  useShortMonth,
}: {
  date: DateInput;
  locale: I18nLocale;
  timezone?: string | null;
  useShortMonth?: boolean;
}) {
  return intlFormat(
    normalizeDateInput(date),
    {
      year: 'numeric',
      month: useShortMonth ? 'short' : 'long',
      day: 'numeric',
      timeZone: timezone ?? undefined,
    },
    {
      locale: getLocaleCode(locale),
    },
  );
}

const SHORTER_DATE_FORMAT = {
  en: 'M/d/yyyy',
  es: 'd/M/yyyy',
  pt: 'd/M/yyyy',
} satisfies Record<I18nLocale, string>;

function shorterDate({
  date,
  locale,
}: {
  date: DateInput;
  locale: I18nLocale;
}) {
  return formatWithDateFnsPattern({
    date,
    locale,
    pattern: SHORTER_DATE_FORMAT[locale],
  });
}

const SHORTEST_DATE_WITH_YEAR_FORMAT = {
  en: 'M/d/yy',
  es: 'd/M/yy',
  pt: 'd/M/yy',
} satisfies Record<I18nLocale, string>;

function shortestDateWithYear({
  date,
  locale,
}: {
  date: DateInput;
  locale: I18nLocale;
}) {
  return formatWithDateFnsPattern({
    date,
    locale,
    pattern: SHORTEST_DATE_WITH_YEAR_FORMAT[locale],
  });
}

function shorterTime({
  date,
  locale,
  timezone,
  displayTimezone,
}: {
  date: DateInput;
  locale: I18nLocale;
  timezone?: string | null;
  displayTimezone?: boolean;
}) {
  return formatInTimeZone({
    instant: date,
    locale,
    timeZone: timezone,
    displayTimezone,
  });
}

const DATETIME_FORMAT = {
  en: 'MM/dd/yyyy  hh:mma',
  es: 'dd/MM/yyyy HH:mm',
  pt: 'dd/MM/yyyy HH:mm',
} satisfies Record<I18nLocale, string>;

function dateTime({ date, locale }: { date: DateInput; locale: I18nLocale }) {
  return formatWithDateFnsPattern({
    date,
    locale,
    pattern: DATETIME_FORMAT[locale],
  });
}

const SHORT_DATETIME_FORMAT_BY_LOCALE = {
  en: 'M/d/yy h:mma',
  es: 'd/M/yy HH:mm',
  pt: 'd/M/yy HH:mm',
} satisfies Record<I18nLocale, string>;

function shortDateTime({
  date,
  locale,
}: {
  date: DateInput;
  locale: I18nLocale;
}) {
  return formatWithDateFnsPattern({
    date,
    locale,
    pattern: SHORT_DATETIME_FORMAT_BY_LOCALE[locale],
  });
}

function humanDate({
  date,
  locale,
  timezone,
  opts = {},
}: {
  date: DateInput;
  locale: I18nLocale;
  timezone?: string | null;
  opts?: {
    shortMonth?: boolean;
  };
}) {
  const parsedDate = normalizeDateInput(date);
  if (!isValid(parsedDate)) {
    return 'Invalid Date';
  }

  return formatCalendarDateWithIntl({
    date: parsedDate,
    locale,
    timezone,
    useShortMonth: opts?.shortMonth,
  });
}

const HUMAN_DATETIME_FORMAT = {
  en: 'MMM d, yyyy h:mm a',
  es: 'MMM d, yyyy H:mm',
  pt: 'MMM d, yyyy H:mm',
} satisfies Record<I18nLocale, string>;

export function formatInstantDateTimeLong({
  date,
  locale,
}: {
  date: DateInput;
  locale: I18nLocale;
}) {
  return formatWithDateFnsPattern({
    date,
    locale,
    pattern: HUMAN_DATETIME_FORMAT[locale],
  });
}

export function formatCalendarMonthAndYear({
  date,
  locale,
}: {
  date: DateInput;
  locale: I18nLocale;
}) {
  return formatWithDateFnsPattern({
    date,
    locale,
    pattern: 'MMMM yyyy',
  });
}

export function formatCalendarMonthAndYearShort({
  date,
  locale,
}: {
  date: DateInput;
  locale: I18nLocale;
}) {
  return formatWithDateFnsPattern({
    date,
    locale,
    pattern: 'MMM yyyy',
  });
}

export function formatCalendarDate(args: Parameters<typeof humanDate>[0]) {
  return humanDate(args);
}

export function formatCalendarDateShort(
  args: Parameters<typeof shorterDate>[0],
) {
  return shorterDate(args);
}

export function formatCalendarDateWithYear(
  args: Parameters<typeof shortestDateWithYear>[0],
) {
  return shortestDateWithYear(args);
}

export function formatInstantDateTime(args: Parameters<typeof dateTime>[0]) {
  return dateTime(args);
}

export function formatInstantDateTimeShort(
  args: Parameters<typeof shortDateTime>[0],
) {
  return shortDateTime(args);
}

export function formatInstantTime(args: Parameters<typeof shorterTime>[0]) {
  return shorterTime(args);
}
